import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, Users, Briefcase, User, DollarSign, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import { RowsSkeleton } from '../shared/Skeleton';
import { useForecastV3 } from '../../context/ForecastV3Context';
import { defaultMonthForYear } from '../../utils';
import { getLaborDetailData, type LaborDetailAccount, type LaborDetailPosition, type LaborDetailEmployee, type LaborDetailMeta } from '../../api';

// Types for labor detail data (matches API response)
interface Employee {
  id: number;
  name: string;
  actualHours: number;
  actualWages: number;
  avgRate: number;
  hireDate: string | null;
  status: string;
}

interface Position {
  id: number;
  name: string;
  departmentName: string;
  actualHours: number;
  projectedHours: number;
  actualWages: number;
  projectedWages: number;
  totalHours: number;
  totalWages: number;
  hpor: number;
  dpor: number;
  employees: Employee[];
}

interface GLAccount {
  id: number | null;
  accountNumber: string;
  accountName: string;
  actualHours: number;
  projectedHours: number;
  actualWages: number;
  projectedWages: number;
  totalHours: number;
  totalWages: number;
  hpor: number;
  dpor: number;
  positions: Position[];
}

// Transform API response to component format
function transformApiData(data: LaborDetailAccount[]): GLAccount[] {
  return data.map(account => ({
    id: account.chartOfAccountId,
    accountNumber: account.accountNumber,
    accountName: account.accountName,
    actualHours: account.actualHours,
    projectedHours: account.projectedHours,
    actualWages: account.actualWages,
    projectedWages: account.projectedWages,
    totalHours: account.totalHours,
    totalWages: account.totalWages,
    hpor: account.hpor,
    dpor: account.dpor,
    positions: account.positions.map(pos => ({
      id: pos.positionId,
      name: pos.positionName,
      departmentName: pos.departmentName,
      actualHours: pos.actualHours,
      projectedHours: pos.projectedHours,
      actualWages: pos.actualWages,
      projectedWages: pos.projectedWages,
      totalHours: pos.totalHours,
      totalWages: pos.totalWages,
      hpor: pos.hpor,
      dpor: pos.dpor,
      employees: pos.employees.map(emp => ({
        id: emp.employeeId,
        name: emp.fullName,
        actualHours: emp.actualHours,
        actualWages: emp.actualWages,
        avgRate: emp.avgHourlyRate,
        hireDate: emp.hireDate,
        status: emp.status,
      })),
    })),
  }));
}

// Month selector component
function MonthSelector({ selectedMonth, onMonthChange }: { selectedMonth: number; onMonthChange: (month: number) => void }) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <select
      value={selectedMonth}
      onChange={(e) => onMonthChange(parseInt(e.target.value))}
      className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none"
      onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px var(--lodge-orange-400)'; e.currentTarget.style.borderColor = 'transparent'; }}
      onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
    >
      {months.map((month, index) => (
        <option key={index + 1} value={index + 1}>{month}</option>
      ))}
    </select>
  );
}

// Employee row component
function EmployeeRow({ employee, isLast }: { employee: Employee; isLast: boolean }) {
  return (
    <tr className="bg-slate-50/50">
      <td className={`py-2 pl-16 text-sm text-slate-600 ${!isLast ? 'border-b border-slate-100' : ''}`}>
        <div className="flex items-center gap-2">
          <User size={12} className="text-slate-400" />
          {employee.name}
          {employee.avgRate > 0 && (
            <span className="text-xs text-slate-400">
              (${employee.avgRate.toFixed(2)}/hr)
            </span>
          )}
        </div>
      </td>
      <td className={`py-2 px-4 text-right text-sm text-slate-600 ${!isLast ? 'border-b border-slate-100' : ''}`}>
        {employee.actualHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
      </td>
      <td className={`py-2 px-4 text-right text-sm text-slate-400 ${!isLast ? 'border-b border-slate-100' : ''}`}>--</td>
      <td className={`py-2 px-4 text-right text-sm text-slate-600 ${!isLast ? 'border-b border-slate-100' : ''}`}>
        {employee.actualHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
      </td>
      <td className={`py-2 px-4 text-right text-sm text-slate-600 ${!isLast ? 'border-b border-slate-100' : ''}`}>
        ${employee.actualWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </td>
      <td className={`py-2 px-4 text-right text-sm text-slate-400 ${!isLast ? 'border-b border-slate-100' : ''}`}>--</td>
      <td className={`py-2 px-4 text-right text-sm text-slate-600 ${!isLast ? 'border-b border-slate-100' : ''}`}>
        ${employee.actualWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </td>
      <td className={`py-2 px-4 text-right text-sm text-slate-400 ${!isLast ? 'border-b border-slate-100' : ''}`}>--</td>
      <td className={`py-2 px-4 text-right text-sm text-slate-400 ${!isLast ? 'border-b border-slate-100' : ''}`}>--</td>
    </tr>
  );
}

// Position row component
function PositionRow({
  position,
  isExpanded,
  onToggle,
  showEmployees,
}: {
  position: Position;
  isExpanded: boolean;
  onToggle: () => void;
  showEmployees: boolean;
}) {
  return (
    <>
      <tr className="bg-white hover:bg-slate-50 transition-colors">
        <td className="py-2 pl-10 border-b border-slate-100">
          <button
            onClick={onToggle}
            className="flex items-center gap-2 text-sm font-medium text-slate-700"
            disabled={!showEmployees || position.employees.length === 0}
          >
            {showEmployees && position.employees.length > 0 && (
              isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
            )}
            <Briefcase size={14} className="text-slate-400" />
            {position.name}
            <span className="text-xs text-slate-400">({position.employees.length})</span>
          </button>
        </td>
        <td className="py-2 px-4 text-right text-sm text-slate-700 border-b border-slate-100">
          {position.actualHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        </td>
        <td className="py-2 px-4 text-right text-sm text-slate-500 border-b border-slate-100">
          {position.projectedHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        </td>
        <td className="py-2 px-4 text-right text-sm font-medium text-slate-800 border-b border-slate-100">
          {position.totalHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        </td>
        <td className="py-2 px-4 text-right text-sm text-slate-700 border-b border-slate-100">
          ${position.actualWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </td>
        <td className="py-2 px-4 text-right text-sm text-slate-500 border-b border-slate-100">
          ${position.projectedWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </td>
        <td className="py-2 px-4 text-right text-sm font-medium text-slate-800 border-b border-slate-100">
          ${position.totalWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </td>
        <td className="py-2 px-4 text-right text-sm text-slate-500 border-b border-slate-100">
          {position.hpor.toFixed(3)}
        </td>
        <td className="py-2 px-4 text-right text-sm text-slate-500 border-b border-slate-100">
          ${position.dpor.toFixed(2)}
        </td>
      </tr>
      {isExpanded && showEmployees && position.employees.map((employee, idx) => (
        <EmployeeRow
          key={employee.id}
          employee={employee}
          isLast={idx === position.employees.length - 1}
        />
      ))}
    </>
  );
}

// GL Account row component
function GLAccountRow({
  account,
  isExpanded,
  onToggle,
  expandedPositions,
  onTogglePosition,
  showEmployees,
}: {
  account: GLAccount;
  isExpanded: boolean;
  onToggle: () => void;
  expandedPositions: Set<number>;
  onTogglePosition: (positionId: number) => void;
  showEmployees: boolean;
}) {
  return (
    <>
      <tr className="bg-slate-100 hover:bg-slate-200/70 transition-colors">
        <td className="py-3 px-4 border-b border-slate-200">
          <button
            onClick={onToggle}
            className="flex items-center gap-2 font-medium text-slate-800"
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <DollarSign size={16} className="text-rose-500" />
            <span className="text-sm text-slate-500 font-mono">{account.accountNumber}</span>
            <span className="text-sm">{account.accountName}</span>
            <span className="text-xs text-slate-400">({account.positions.length} positions)</span>
          </button>
        </td>
        <td className="py-3 px-4 text-right font-medium text-slate-700 border-b border-slate-200">
          {account.actualHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        </td>
        <td className="py-3 px-4 text-right text-slate-600 border-b border-slate-200">
          {account.projectedHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        </td>
        <td className="py-3 px-4 text-right font-semibold text-slate-800 border-b border-slate-200">
          {account.totalHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
        </td>
        <td className="py-3 px-4 text-right font-medium text-slate-700 border-b border-slate-200">
          ${account.actualWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </td>
        <td className="py-3 px-4 text-right text-slate-600 border-b border-slate-200">
          ${account.projectedWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </td>
        <td className="py-3 px-4 text-right font-semibold text-slate-800 border-b border-slate-200">
          ${account.totalWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </td>
        <td className="py-3 px-4 text-right text-slate-600 border-b border-slate-200">
          {account.hpor.toFixed(3)}
        </td>
        <td className="py-3 px-4 text-right text-slate-600 border-b border-slate-200">
          ${account.dpor.toFixed(2)}
        </td>
      </tr>
      {isExpanded && account.positions.map((position) => (
        <PositionRow
          key={position.id}
          position={position}
          isExpanded={expandedPositions.has(position.id)}
          onToggle={() => onTogglePosition(position.id)}
          showEmployees={showEmployees}
        />
      ))}
    </>
  );
}

// MTD Pace Card component
function MTDPaceCard({ data, meta }: { data: GLAccount[]; meta: LaborDetailMeta | null }) {
  const totals = useMemo(() => {
    const sums = data.reduce(
      (acc, account) => ({
        actualHours: acc.actualHours + account.actualHours,
        projectedHours: acc.projectedHours + account.projectedHours,
        actualWages: acc.actualWages + account.actualWages,
        projectedWages: acc.projectedWages + account.projectedWages,
        totalHours: acc.totalHours + account.totalHours,
        totalWages: acc.totalWages + account.totalWages,
      }),
      { actualHours: 0, projectedHours: 0, actualWages: 0, projectedWages: 0, totalHours: 0, totalWages: 0 }
    );
    // Calculate overall HPOR and DPOR from totals
    const occupiedRooms = meta?.occupiedRooms || 0;
    return {
      ...sums,
      hpor: occupiedRooms > 0 ? sums.actualHours / occupiedRooms : 0,
      dpor: occupiedRooms > 0 ? sums.actualWages / occupiedRooms : 0,
    };
  }, [data, meta]);

  const daysElapsed = meta?.daysElapsed || 0;
  const daysInMonth = meta?.daysInMonth || 31;
  const isCurrentMonth = meta?.isCurrentMonth || false;
  const occupiedRooms = meta?.occupiedRooms || 0;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
      <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <TrendingUp size={16} style={{ color: 'var(--lodge-orange-400)' }} />
        MTD Pace {isCurrentMonth && <span className="text-xs font-normal" style={{ color: 'var(--lodge-orange-400)' }}>(Current Month)</span>}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <div className="text-xs text-slate-500 mb-1">Actual Hours MTD</div>
          <div className="text-lg font-semibold text-slate-800">
            {totals.actualHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Actual Cost MTD</div>
          <div className="text-lg font-semibold text-slate-800">
            ${totals.actualWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Occupied Rooms</div>
          <div className="text-lg font-semibold text-slate-800">
            {occupiedRooms.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Days Elapsed</div>
          <div className="text-lg font-semibold text-slate-800">
            {daysElapsed} <span className="text-sm font-normal text-slate-500">of {daysInMonth}</span>
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Projected Remaining</div>
          <div className="text-lg font-semibold text-slate-800">
            ${totals.projectedWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>
      <div className="border-t border-slate-100 mt-3 pt-3">
        <div className="flex flex-wrap gap-6">
          <div>
            <div className="text-xs text-slate-500 mb-1">Projected Month-End</div>
            <div className="flex gap-4">
              <div>
                <span className="text-sm text-slate-600">Hours: </span>
                <span className="font-semibold">{totals.totalHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
              </div>
              <div>
                <span className="text-sm text-slate-600">Cost: </span>
                <span className="font-semibold">${totals.totalWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
            </div>
          </div>
          <div className="border-l border-slate-200 pl-6">
            <div className="text-xs text-slate-500 mb-1">Labor Metrics</div>
            <div className="flex gap-4">
              <div>
                <span className="text-sm text-slate-600">HPOR: </span>
                <span className="font-semibold">{totals.hpor.toFixed(3)}</span>
              </div>
              <div>
                <span className="text-sm text-slate-600">DPOR: </span>
                <span className="font-semibold">${totals.dpor.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LaborDetailPage() {
  const { state } = useForecastV3();
  const { year, orgId, locationId } = state;
  const [selectedMonth, setSelectedMonth] = useState(() => defaultMonthForYear(year));
  const [showEmployees, setShowEmployees] = useState(true);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number | null>>(new Set());
  const [expandedPositions, setExpandedPositions] = useState<Set<number>>(new Set());

  // Fetch labor detail data
  const { data: laborResponse, isLoading, error, isError } = useQuery({
    queryKey: ['laborDetail', orgId, locationId, year, selectedMonth],
    queryFn: () => getLaborDetailData({
      orgId: orgId!,
      locationId: locationId!,
      year,
      month: selectedMonth
    }),
    enabled: !!orgId && !!locationId && !!year,
    staleTime: 30000, // 30 seconds
  });

  // Transform API data
  const laborData = useMemo(() => {
    if (!laborResponse?.data) return [];
    return transformApiData(laborResponse.data);
  }, [laborResponse?.data]);

  const meta = laborResponse?.meta || null;

  // Auto-expand first account when data loads
  useMemo(() => {
    if (laborData.length > 0 && expandedAccounts.size === 0) {
      setExpandedAccounts(new Set([laborData[0].id]));
    }
  }, [laborData]);

  const toggleAccount = useCallback((accountId: number | null) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  }, []);

  const togglePosition = useCallback((positionId: number) => {
    setExpandedPositions((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) {
        next.delete(positionId);
      } else {
        next.add(positionId);
      }
      return next;
    });
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    const sums = laborData.reduce(
      (acc, account) => ({
        actualHours: acc.actualHours + account.actualHours,
        projectedHours: acc.projectedHours + account.projectedHours,
        actualWages: acc.actualWages + account.actualWages,
        projectedWages: acc.projectedWages + account.projectedWages,
        totalHours: acc.totalHours + account.totalHours,
        totalWages: acc.totalWages + account.totalWages,
      }),
      { actualHours: 0, projectedHours: 0, actualWages: 0, projectedWages: 0, totalHours: 0, totalWages: 0 }
    );
    // Calculate overall HPOR and DPOR from totals
    const occupiedRooms = meta?.occupiedRooms || 0;
    return {
      ...sums,
      hpor: occupiedRooms > 0 ? sums.actualHours / occupiedRooms : 0,
      dpor: occupiedRooms > 0 ? sums.actualWages / occupiedRooms : 0,
    };
  }, [laborData, meta]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="text-rose-500" size={24} />
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Labor Detail</h1>
              <p className="text-sm text-slate-500">
                {monthNames[selectedMonth - 1]} {year} • Breakdown by GL Account, Position, and Employee
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MonthSelector selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showEmployees}
                onChange={(e) => setShowEmployees(e.target.checked)}
                className="rounded border-slate-300"
                style={{ accentColor: 'var(--lodge-orange-400)' }}
              />
              Show Employees
            </label>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-slate-50">
        {/* Loading State */}
        {isLoading && (
          <div className="rn-sk-inset-only fv3-sk-frame"><RowsSkeleton rows={10} cols={7} label="Loading labor data" /></div>
        )}
        {isLoading && (
          <div className="rn-sk-classic-only flex items-center justify-center py-12">
            <Loader2 className="animate-spin mr-2" size={24} style={{ color: 'var(--lodge-orange-400)' }} />
            <span className="text-slate-600">Loading labor data...</span>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <div>
              <p className="text-sm font-medium text-red-800">Failed to load labor data</p>
              <p className="text-sm text-red-600">{(error as Error)?.message || 'Please try again later.'}</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && laborData.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <Users className="mx-auto text-slate-300 mb-3" size={48} />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No Labor Data</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              No payroll data found for {monthNames[selectedMonth - 1]} {year}.
              Labor data will appear here once PayrollCost records are imported and departments are mapped to GL accounts.
            </p>
          </div>
        )}

        {/* Data Display */}
        {!isLoading && !isError && laborData.length > 0 && (
          <>
            {/* MTD Pace Card */}
            <MTDPaceCard data={laborData} meta={meta} />

            {/* Main Table */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Account / Position
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Actual Hrs
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Proj Hrs
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Total Hrs
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Actual $
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Proj $
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Total $
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      HPOR
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      DPOR
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {laborData.map((account) => (
                    <GLAccountRow
                      key={account.id || 'unmapped'}
                      account={account}
                      isExpanded={expandedAccounts.has(account.id)}
                      onToggle={() => toggleAccount(account.id)}
                      expandedPositions={expandedPositions}
                      onTogglePosition={togglePosition}
                      showEmployees={showEmployees}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-800 text-white">
                    <td className="py-3 px-4 font-semibold">TOTAL</td>
                    <td className="py-3 px-4 text-right font-semibold">
                      {totals.actualHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">
                      {totals.projectedHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </td>
                    <td className="py-3 px-4 text-right font-bold">
                      {totals.totalHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">
                      ${totals.actualWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">
                      ${totals.projectedWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-3 px-4 text-right font-bold">
                      ${totals.totalWages.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">
                      {totals.hpor.toFixed(3)}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">
                      ${totals.dpor.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LaborDetailPage;
